/**
 * Media API - Common Types
 *
 * Enums, shared schemas, and entity re-exports used across media endpoints.
 */

import { z } from 'zod'
import { MediaSchema, MediaReportSchema, MediaReportStatusSchema } from '../../schemas/entities/media'
import { MediaTypeSchema } from '../../schemas/entities/collection'

// Re-export entity types. `MediaReportStatusSchema` is the canonical media-report
// status enum owned by the entity layer (schemas/entities/media.ts) — re-exported
// here (same symbol) so the media API reuses it rather than redefining a duplicate.
export { MediaSchema, MediaReportSchema, MediaReportStatusSchema }
export type { Media, MediaReport, MediaReportStatus } from '../../schemas/entities/media'
export { MediaTypeSchema }
export type { MediaType } from '../../schemas/entities/collection'

// ============================================================================
// Enums
// ============================================================================

export const MediaVisibilitySchema = z.enum(['PUBLIC', 'PRIVATE', 'SCHOOL', 'COLLECTION_ONLY'])
export type MediaVisibility = z.infer<typeof MediaVisibilitySchema>

export const ProcessingStatusSchema = z.enum(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'])
export type ProcessingStatus = z.infer<typeof ProcessingStatusSchema>

export const FaceDetectionStatusSchema = z.enum(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'SKIPPED'])
export type FaceDetectionStatus = z.infer<typeof FaceDetectionStatusSchema>

export const TagTypeSchema = z.enum(['KEYWORD', 'LOCATION', 'EVENT'])
export type TagType = z.infer<typeof TagTypeSchema>

// NOTE (SCH-446): media-domain report symbols are `Media`-prefixed to avoid a
// package-root name collision with the unified moderation domain (api/moderation/**),
// which exports its own lowercase `ReportReasonSchema`/`ReportStatusSchema`/etc. Both
// domains star-export to the package root; distinct names keep root imports unambiguous.
// (`MediaReportStatusSchema` is re-exported from the entity layer above.) The uppercase,
// privacy/copyright-oriented reason set below is the legacy media-report set.
export const MediaReportReasonSchema = z.enum(['INAPPROPRIATE', 'PRIVACY', 'COPYRIGHT', 'BULLYING', 'OTHER'])
export type MediaReportReason = z.infer<typeof MediaReportReasonSchema>

export const ReportResolutionSchema = z.enum(['REMOVED', 'HIDDEN', 'NO_ACTION', 'WARNING_ISSUED'])
export type ReportResolution = z.infer<typeof ReportResolutionSchema>

export const ResolveStatusSchema = z.enum(['RESOLVED', 'DISMISSED'])
export type ResolveStatus = z.infer<typeof ResolveStatusSchema>

export const VerificationStatusSchema = z.enum(['PENDING', 'CONFIRMED', 'REJECTED', 'EXPIRED', 'ALL'])
export type VerificationStatus = z.infer<typeof VerificationStatusSchema>

export const FaceMatchStatusSchema = z.enum(['UNMATCHED', 'SUGGESTED', 'CONFIRMED', 'REJECTED', 'UNKNOWN'])
export type FaceMatchStatus = z.infer<typeof FaceMatchStatusSchema>

/**
 * Why a media upload happened — stamped onto `Media.uploadContext` and read by
 * the filing/authorization/retention paths (see `authorize-upload-context.ts`).
 *
 * `SUPPORT_REQUEST` — screenshots attached to a support contact request. The
 * value is deliberately its OWN member rather than a reuse of an existing one:
 * these uploads are never filed into a collection and must stay visible to
 * their uploader alone, so `CHAT_MESSAGE` (which grants a same-school read
 * allowance in `get-media.ts`) would widen access, and `CONTENT_POST` /
 * `MEDIA_LIBRARY` would mislabel a row that the space-keyed filing gates never
 * govern. The column is `VarChar(50)`, not a DB enum — adding a member needs
 * no migration.
 */
export const UploadContextTypeSchema = z.enum(['CONTENT_POST', 'CHAT_MESSAGE', 'MEDIA_LIBRARY', 'SUPPORT_REQUEST'])
export type UploadContextType = z.infer<typeof UploadContextTypeSchema>

// ============================================================================
// Shared Nested Schemas
// ============================================================================

export const MediaUploaderSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  profileImageUrl: z.string().nullable().optional()
})

export type MediaUploader = z.infer<typeof MediaUploaderSchema>

export const UploadContextSchema = z.object({
  type: UploadContextTypeSchema,
  groupId: z.string().optional(),
  contentRecordId: z.string().optional(),
  collectionId: z.string().optional()
})

export type UploadContext = z.infer<typeof UploadContextSchema>
